import { useMemo } from 'react';
import type { IntlShape } from 'react-intl';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { useModal } from '~/eds/src/components/hooks/useModal';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Markdown } from '~/eds/src/components/Markdown/Markdown';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { PageLayout } from '~/eds/src/modules/PageLayout/PageLayout';
import { EntityType } from '~/enums/EntityType';
import { CreditBalance } from '~/features/x-ray/billing/CreditBalance';
import { useGetModelsSummary } from '~/features/x-ray/billing/hooks';
import { ModelQuotaUsage } from '~/features/x-ray/billing/ModelQuotaUsage';
import { useCreditStatusMessage } from '~/features/x-ray/billing/useCreditStatusMessage';
import { getQuotaStatus } from '~/features/x-ray/billing/utils';
import { useCreateAction } from '~/features/x-ray/hooks/useCreateAction';
import { usePollModelProgress } from '~/features/x-ray/hooks/usePollModelProgress';
import { useResolveTargetEntityDetails } from '~/features/x-ray/hooks/useResolveTargetEntityDetails';
import { useSessionStorage } from '~/features/x-ray/hooks/useSessionStorage';
import { ModelModal } from '~/features/x-ray/modals/ModelModal';
import { ModelResults } from '~/features/x-ray/ModelResults';
import { ModelStatusSummary } from '~/features/x-ray/ModelStatusSummary';
import { ModelVersionInfo } from '~/features/x-ray/ModelVersionInfo';
import { TabKey } from '~/features/x-ray/types/model';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { slices } from '~/redux/slices';
import { RoutePathType } from '~/routing/enums';
import { useRouting } from '~/routing/useRouting';

const Page = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { location, navigate, getSearchParams, setSearchParams } = useRouting();
  const searchParam = getSearchParams();
  const enableTableExtraction = useFlag(FlagType.CustomAiTableExtraction);
  const enableXRayLibrary = useFlag(FlagType.DocumentXRayPrebuiltModels);
  const enableXrayNewPricingModel = useFlag(
    FlagType.DocumentXrayNewPricingModel,
  );

  const { isLoading: isLoadingSummary, summary } = useGetModelsSummary();

  const {
    total,
    published,
    suspended,
    deleted,
    draft,
    consumedQuota,
    totalQuota,
  } = summary;

  const status = getQuotaStatus({
    consumedQuota,
    quotaCount: totalQuota,
  });

  const activeModelResultInfo = useSelector(
    slices.xRay.selectors.selectActiveModelResultInfo,
  );

  const [targetEntityDetails] = useResolveTargetEntityDetails(
    activeModelResultInfo?.targetEntity ?? null,
  );

  const [deletedStatusIsDismissed, setDeletedStatusIsDismissed] =
    useSessionStorage('deletedStatusIsDismissed', false);

  const [totalModelsModal, showTotalModels] = useModal({
    title: intl.formatMessage({
      id: 'FieldsPage.totalModelsTitle',
      defaultMessage: 'Total Models',
      description: 'Title for total models modal',
    }),
    children: (
      <Layout preset="sections" p={4}>
        <Markdown
          text={intl.formatMessage({
            id: 'FieldsPage.totalModelsInfoText',
            defaultMessage:
              'Only published models count towards the total (this includes In Progress, Suspended, and models that were published then deleted). Draft models do not count towards the total.',
            description: 'Text for total models info',
          })}
        />
        <ModelQuotaUsage
          isLoading={isLoadingSummary}
          consumedQuota={consumedQuota}
          max={totalQuota}
          status={status}
        />
        <ModelStatusSummary />
      </Layout>
    ),
  });

  const activeTargetEntityDetails =
    activeModelResultInfo?.targetEntityDetails ?? targetEntityDetails;

  const creditStatusMessage = useCreditStatusMessage();

  const { progressChip } = usePollModelProgress({
    modelId: activeModelResultInfo?.id,
  });

  const { entityType, title } = resolvePageProps(location.pathname, intl);

  const createAction = useCreateAction(entityType);
  const createTableAction = useCreateAction(EntityType.DocumentAttachedData);
  const browseAction = {
    level: 'primary' as const,
    text: intl.formatMessage({
      id: 'FieldsPage.browseModelLibrary',
      defaultMessage: 'Browse Model Library',
      description: 'Text for browse model library button',
    }),
    onClick: () => navigate(RoutePathType.XRayLibrary),
  };
  const actions =
    enableXRayLibrary || enableTableExtraction
      ? [
          { ...createAction, level: 'secondary' as const },
          ...(enableTableExtraction
            ? [{ ...createTableAction, level: 'secondary' as const }]
            : []),
          ...(enableXRayLibrary ? [browseAction] : []),
        ]
      : [createAction];

  const panel = activeModelResultInfo
    ? {
        children: (
          <ModelVersionInfo
            enableTitle
            modelId={activeModelResultInfo.id}
            version={activeModelResultInfo.latestVersion}
          />
        ),
        chips: [progressChip],
        hidden: {
          isHidden: false,
          onHide: () =>
            dispatch(slices.xRay.actions.setActiveModelResultInfo(null)),
        },
        icon: activeModelResultInfo.libraryModelId
          ? {
              icon: 'library' as const,
              tooltip: intl.formatMessage({
                id: 'FieldsPage.modelHeaderLibraryIconTooltip',
                defaultMessage: 'From model library',
                description: 'Tooltip for models from library icon',
              }),
            }
          : undefined,
        title: activeTargetEntityDetails?.label ?? '–',
        width: 'm' as const,
      }
    : undefined;

  const quotaStatusMessage = (tabKey: TabKey) => {
    switch (tabKey) {
      case 'deleted':
        if (deletedStatusIsDismissed) return null;
        else
          return (
            <StatusMessage
              message={intl.formatMessage({
                id: 'FieldsPage.deletedModelsQuotaStatus',
                defaultMessage:
                  'Models that were either published or suspended then deleted continue to count towards the model quota and are not refundable.',
                description: 'Message for deleted models quota status',
              })}
              status="info"
              onDismiss={() => {
                setDeletedStatusIsDismissed(true);
              }}
            />
          );
      default:
        return null;
    }
  };

  const tabs = useMemo(() => {
    const getModelResults = (tabKey: TabKey) => {
      return (
        <Layout preset="sections">
          {enableXrayNewPricingModel ? (
            quotaStatusMessage(tabKey)
          ) : creditStatusMessage ? (
            <StatusMessage
              message={creditStatusMessage?.message}
              status={creditStatusMessage?.status}
              title={creditStatusMessage?.title}
            />
          ) : undefined}
          <ModelResults
            entityType={entityType}
            onShowResult={(modelResult) =>
              dispatch(
                slices.xRay.actions.setActiveModelResultInfo({
                  id: modelResult.id,
                  latestVersion: modelResult.latestVersion,
                  libraryModelId: modelResult.libraryModelId,
                  targetEntity: modelResult.targetEntity,
                  targetEntityDetails: modelResult.targetEntityDetails,
                }),
              )
            }
          />
          <ModelModal />
        </Layout>
      );
    };

    const tabs = [
      {
        label: intl.formatMessage({
          id: 'FieldsPage.allModels',
          defaultMessage: 'All models',
          description: 'Label for all models tab',
        }),
        panel: getModelResults('all'),
        value: 'all',
        count: total - deleted,
      },
      {
        label: intl.formatMessage({
          id: 'FieldsPage.publishedModels',
          defaultMessage: 'Published',
          description: 'Label for published models tab',
        }),
        panel: getModelResults('published'),
        value: 'published',
        count: published,
        countStatus: 'success' as const,
      },
      {
        label: intl.formatMessage({
          id: 'FieldsPage.suspendedModels',
          defaultMessage: 'Suspended',
          description: 'Label for suspended models tab',
        }),
        panel: getModelResults('suspended'),
        value: 'suspended',
        count: suspended,
        countStatus: 'danger' as const,
      },
      {
        label: intl.formatMessage({
          id: 'FieldsPage.drafts',
          defaultMessage: 'Drafts',
          description: 'Label for drafts tab',
        }),
        panel: getModelResults('drafts'),
        value: 'drafts',
        count: draft,
        countStatus: 'warning' as const,
      },
      {
        label: intl.formatMessage({
          id: 'FieldsPage.deletedModels',
          defaultMessage: 'Deleted',
          description: 'Label for deleted models tab',
        }),
        panel: getModelResults('deleted'),
        value: 'deleted',
        count: searchParam.tab === 'deleted' ? deleted : undefined,
      },
    ];

    return {
      tabs: tabs.map((tab) => tab),
      selectedTab: (searchParam.tab as string) ?? 'all',
      onSelectTab: (updatedTab: string) => {
        setSearchParams({
          tab: updatedTab,
          pageIndex: '1',
        });
      },
    };
  }, [
    isLoadingSummary,
    summary,
    enableXrayNewPricingModel,
    creditStatusMessage,
  ]);

  return (
    <PageLayout
      actions={actions}
      headerCalloutContent={
        enableXrayNewPricingModel ? (
          <>
            <ModelQuotaUsage
              isLoading={isLoadingSummary}
              consumedQuota={consumedQuota}
              max={totalQuota}
              status={status}
              mode="compact"
              onClick={showTotalModels}
            />
            {totalModelsModal}
          </>
        ) : (
          <CreditBalance />
        )
      }
      panel={panel}
      tabs={tabs}
      title={title}
    >
      {totalModelsModal}
    </PageLayout>
  );
};

export default Page;

const resolvePageProps = (
  pathname: string,
  intl: IntlShape,
): {
  entityType: EntityType.Field;
  title: string;
} => {
  switch (pathname) {
    case RoutePathType.AutomationHubFields:
    default:
      return {
        entityType: EntityType.Field,
        title: intl.formatMessage({
          id: 'FieldsPage.automationHubTitle',
          defaultMessage: 'Automation Hub',
          description: 'Title for the Automation Hub page',
        }),
      };
  }
};
